import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cake, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/PersonCard";
import { t } from "@/lib/i18n";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "long" });
const when = (days) => (days === 0 ? t("Today") : days === 1 ? t("Tomorrow") : `In ${days} days`);
const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;

const EventRow = ({ e }) => {
  const bday = e.type === "birthday";
  const Icon = bday ? Cake : Heart;
  return (
    <div className={`flex items-center gap-5 p-5 rounded-3xl border bg-white ${e.days === 0 ? "border-[#D96B43] ring-4 ring-[#D96B43]/10" : "border-[#E5DEC9]"}`} data-testid={`celebration-${e.type}-${e.people[0].person_id}`}>
      <div className="w-16 text-center shrink-0">
        <p className="font-serif text-2xl font-bold text-[#1E3A2B]">{new Date(e.date + "T00:00:00").getDate()}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7E8E85]">{new Date(e.date + "T00:00:00").toLocaleDateString(undefined, { month: "short" })}</p>
      </div>
      <div className="flex -space-x-3">{e.people.map((p) => <Avatar key={p.person_id} p={p} size="w-12 h-12" />)}</div>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-lg font-semibold truncate">
          {e.people.map((p, i) => <span key={p.person_id}>{i > 0 && " & "}<Link to={`/person/${p.person_id}`} className="hover:text-[#D96B43]">{p.name}</Link></span>)}
        </p>
        <p className="text-sm text-[#52635B] flex items-center gap-1.5">
          <Icon className={`w-4 h-4 ${bday ? "text-[#C59B27]" : "text-[#D96B43]"}`} />
          {bday ? `${ordinal(e.count)} birthday` : `${ordinal(e.count)} wedding anniversary`} · {fmt(e.date)}
        </p>
      </div>
      <span className={`text-xs font-medium px-3 py-1.5 rounded-full ${e.days <= 7 ? "bg-[#D96B43] text-white" : "bg-[#F4EDE2] text-[#52635B]"}`}>{when(e.days)}</span>
    </div>
  );
};

export default function Celebrations() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/celebrations").then(({ data }) => setEvents(data)); }, []);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14 space-y-10">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3">{t("Celebrations")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">{t("Upcoming birthdays & anniversaries")}</h1>
        <p className="mt-3 text-[#52635B]">Never miss a chance to call. Add a birth date or wedding date to any profile to see it here.</p>
      </div>
      {events === null ? <div className="h-48 rounded-3xl bg-[#F4EDE2] animate-pulse" /> : events.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#CDBF9F] p-16 text-center text-[#7E8E85]" data-testid="celebrations-empty">{t("No dates added yet.")}</div>
      ) : (
        <div className="space-y-3" data-testid="celebrations-list">{events.map((e, i) => <EventRow key={i} e={e} />)}</div>
      )}
    </div>
  );
}
